import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {useClientV2} from "../../../../utils/hooks";
import {GetCustomerStoreServiceOrderApiResponse} from "./types";
import {GET_STORE_SERVICE_CUSTOMER_ORDER_RQ_KEY} from "../constants";

/**
 * STRANGLER BRIDGE (coach order detail)
 *
 * The coach order-detail read now flows through the feature module
 * (`features/product-services`), which always uses the v2 implementation.
 * The v2 endpoint returns only the work-delivery submissions, so the
 * drawer reads order-level fields from the selected list item and uses this
 * hook only for `order.productServiceDeliveries`. Query key preserved.
 *
 * @deprecated Use `useProductServiceOrder` from `features/product-services`.
 */
export {useGetStoreServiceOrderApi} from "../../../../features/product-services/hooks/useGetStoreServiceOrderApi";

type LegacyCustomerDelivery =
  GetCustomerStoreServiceOrderApiResponse["productServiceDelivery"][number];

/** A row of `GET customer/product-services/order/:orderId` (v2). */
type V2CustomerDeliveryRow = {
  id: number;
  statusId: number;
  statusName: string | null;
  deliveryDate: string | null;
  delivery: {key: string; isUpload: boolean}[] | null;
  customerComment: string | null;
  createdAt: string;
  updatedAt: string;
};

type V2CustomerOrderDetailEnvelope = {
  data?: V2CustomerDeliveryRow[];
};

/**
 * Maps a v2 delivery row onto the legacy `productServiceDelivery` item.
 * Field rename: `customerComment` → `customer_comment`. `userId` is not exposed
 * by the DTO and is not read by any consumer.
 */
const toLegacyCustomerDelivery = (
  row: V2CustomerDeliveryRow,
  orderId: number
): LegacyCustomerDelivery => ({
  id: row.id,
  userId: 0,
  productServiceTransactionId: orderId,
  statusId: row.statusId,
  deliveryDate: row.deliveryDate ?? "",
  delivery: Array.isArray(row.delivery) ? row.delivery : [],
  customer_comment: row.customerComment ?? null,
  createdAt: row.createdAt,
  updatedAt: row.updatedAt,
});

/**
 * Customer order detail — v2 (`GET customer/product-services/order/:orderId`).
 *
 * v2 returns a bare array of delivery DTOs, so this hook supplies only
 * `productServiceDelivery`; the drawer takes the order-level fields and the
 * coach's display name from the selected orders-list item. Signature, query key
 * and `enabled` behavior preserved.
 */
export const useGetCustomerStoreServiceOrderApi = (storeServiceOrderId?: number) => {
  const client = useClientV2();

  return useQuery<
    GetCustomerStoreServiceOrderApiResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: [GET_STORE_SERVICE_CUSTOMER_ORDER_RQ_KEY, String(storeServiceOrderId)],
    queryFn: async () => {
      const orderId = storeServiceOrderId as number;
      const raw = (await client(
        `customer/product-services/order/${orderId}`
      )) as V2CustomerOrderDetailEnvelope;

      const rows = Array.isArray(raw?.data) ? raw.data : [];

      return {
        productServiceDelivery: rows.map((row) => toLegacyCustomerDelivery(row, orderId)),
      };
    },
    enabled: !!storeServiceOrderId,
  });
};
